import { devLog, devError } from '../utils/devLogger';
import { renderNPIEmail } from '../utils/npiEmailTemplate';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Badge,
  Modal,
  ProgressBar,
  ListGroup,
  Tabs,
  Tab,
  Table
} from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import { collection, addDoc, getDocs, query, where, orderBy, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { isExternalFacilitatorRole, isExternalViewerRole } from '../services/roleManagementService';

const CustomEmailComposer = () => {
  const { currentUser, userProfile } = useAuth();
  const externalFacilitatorLimited = isExternalFacilitatorRole(userProfile?.role);
  const externalViewerReporting = isExternalViewerRole(userProfile?.role);
  const [programs, setPrograms] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, variant: '', message: '' });

  // NPI email template constants (absolute URLs required for email images)
  const NPI_LOGO_URL = 'https://lms-access70.web.app/NPI_Logo.png';
  const APP_LOGO_URL = 'https://lms-access70.web.app/Access70Logo.png';
  const APP_URL      = 'https://lms-access70.web.app';

  // Email composition state
  const [emailData, setEmailData] = useState({
    recipientType: 'program', // 'program', 'csv', 'specific'
    selectedProgram: '',
    selectedCohort: '',
    selectedCohorts: [],
    cohortProgramFilter: '',
    specificEmails: '',
    subject: '',
    htmlContent: '',
    textContent: '',
    attachments: [],
    isScheduled: false,
    scheduledDate: '',
    scheduledTime: '',
    isRecurring: false,
    recurringFrequency: 'weekly', // 'daily', 'weekly', 'monthly'
    recurringEndDate: '',
    // NPI template options
    heading: '',
    subtitle: '',
    ctaText: '',
    ctaUrl: '',
    resourceLinks: [], // [{label, url}]
  });

  // Whether to show the template options panel
  const [showTemplateOptions, setShowTemplateOptions] = useState(false);

  // CSV state
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState('');
  const [csvRecipients, setCsvRecipients] = useState([]);

  // Modal states
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendProgress, setSendProgress] = useState({ show: false, current: 0, total: 0 });
  const [sendResults, setSendResults] = useState(null);

  // Scheduled emails
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [showScheduledModal, setShowScheduledModal] = useState(false);
  const [sentEmails, setSentEmails] = useState([]);
  const [showSentEmailsModal, setShowSentEmailsModal] = useState(false);
  const [resendingEmailId, setResendingEmailId] = useState('');

  useEffect(() => {
    if (userProfile) {
      loadFacilitatorPrograms();
      loadCohorts();
    }
    loadScheduledEmails();
    loadSentEmails();
  }, [userProfile]);

  useEffect(() => {
    // External facilitators must not broadcast; force explicit recipients only.
    if (externalFacilitatorLimited) {
      setEmailData((prev) => ({
        ...prev,
        recipientType: 'specific',
        selectedProgram: '',
        selectedCohort: '',
        selectedCohorts: [],
        cohortProgramFilter: '',
        isScheduled: false,
        isRecurring: false
      }));
    }
  }, [externalFacilitatorLimited]);

  const isActiveLearner = (user) => {
    if (!user) return false;
    if (user.isActive === false) return false;
    const status = (user.status || '').toLowerCase();
    if (['inactive', 'dropoff', 'dropped'].includes(status)) return false;
    return true;
  };

  const escapeHtml = (value) => (
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  );

  const normalizeEmailHtml = (value) => {
    const raw = (value || '').trim();
    if (!raw) return '';

    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
    if (hasHtml) {
      return raw;
    }

    const escaped = escapeHtml(raw);
    const paragraphs = escaped.split(/\n\s*\n/).filter(Boolean);
    return paragraphs
      .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br />')}</p>`)
      .join('');
  };

  const stripHtmlToText = (value) => (
    value
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<[^>]*>/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );

  // Branded preview — placed after normalizeEmailHtml so the memo callback never
  // references an uninitialised const (temporal dead zone crash on first render).
  const previewHtml = useMemo(() => {
    if (!emailData.htmlContent && !emailData.heading && !emailData.subtitle) return '';
    return renderNPIEmail({
      orgName:       'NPI Governance Solutions',
      appName:       'Access70 LaunchPad',
      senderEmail:   'launchpad@npigovernancesolutions.com',
      portalUrl:     APP_URL,
      npiLogoUrl:    NPI_LOGO_URL,
      appLogoUrl:    APP_LOGO_URL,
      heading:       emailData.heading  || '',
      subtitle:      emailData.subtitle || '',
      bodyHtml:      normalizeEmailHtml(emailData.htmlContent).replace(/\{name\}/g, 'Learner'),
      ctaText:       emailData.ctaText  || '',
      ctaUrl:        emailData.ctaUrl   || APP_URL,
      resourceLinks: emailData.resourceLinks || [],
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailData.htmlContent, emailData.heading, emailData.subtitle, emailData.ctaText, emailData.ctaUrl, emailData.resourceLinks]);

  const loadScheduledEmails = async () => {
    try {
      if (!currentUser) {
        setScheduledEmails([]);
        return;
      }

      // External facilitators do not have scheduling permissions (and do not have access to scheduledEmails collection).
      if (externalFacilitatorLimited || externalViewerReporting) {
        setScheduledEmails([]);
        return;
      }

      const isAdmin = userProfile?.role === 'admin';
      const baseQuery = collection(db, 'scheduledEmails');
      const q = isAdmin
        ? query(baseQuery, orderBy('scheduledFor', 'desc'))
        : query(
          baseQuery,
          where('createdBy', '==', currentUser.uid),
          orderBy('scheduledFor', 'desc')
        );

      const snapshot = await getDocs(q);
      const emails = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledFor: doc.data().scheduledFor?.toDate()
      }));

      setScheduledEmails(emails);
    } catch (error) {
      devError('Error loading scheduled emails:', error);
    }
  };

  const loadSentEmails = async () => {
    try {
      if (!currentUser) {
        setSentEmails([]);
        return;
      }

      const isAdmin = userProfile?.role === 'admin';
      const baseQuery = collection(db, 'emailLogs');
      const q = isAdmin
        ? query(baseQuery, orderBy('timestamp', 'desc'))
        : query(
          baseQuery,
          where('facilitatorId', '==', currentUser.uid),
          orderBy('timestamp', 'desc')
        );

      const snapshot = await getDocs(q);
      const emails = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        timestamp: docSnap.data().timestamp?.toDate?.() || null
      })).filter((email) => ['custom_email', 'custom_email_resend'].includes(email.type));

      setSentEmails(emails);
    } catch (error) {
      devError('Error loading sent emails:', error);
    }
  };

  const showAlert = (variant, message) => {
    setAlert({ show: true, variant, message });
    setTimeout(() => setAlert({ show: false, variant: '', message: '' }), 5000);
  };

  const loadFacilitatorPrograms = async () => {
    try {
      setLoading(true);

      // Check user authentication
      if (!currentUser) {
        devLog('User not authenticated');
        showAlert('warning', 'Please log in to access programs');
        return;
      }

      // Check if user is admin - admins should see ALL programs
      const isAdmin = userProfile?.role === 'admin';

      if (isAdmin) {
        devLog('Admin user detected - loading ALL programs from Firestore');

        // Load all programs directly from Firestore for admins
        const programsRef = collection(db, 'programs');
        const programsSnapshot = await getDocs(programsRef);

        // Load all cohorts to map cohortId -> programId
        const cohortsRef = collection(db, 'cohorts');
        const cohortsSnapshot = await getDocs(cohortsRef);
        const cohortToProgramMap = {};
        cohortsSnapshot.docs.forEach(doc => {
          const cohortData = doc.data();
          if (cohortData.programId) {
            cohortToProgramMap[doc.id] = cohortData.programId;
          }
        });

        // Also get learner counts per program
        const usersRef = collection(db, 'users');
        const learnersQuery = query(usersRef, where('role', '==', 'learner'));
        const learnersSnapshot = await getDocs(learnersQuery);

        devLog(`Found ${learnersSnapshot.docs.length} total learners`);

        // Count learners per program (check both programId and cohortId->programId)
        const learnerCounts = {};
        learnersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          if (!isActiveLearner(userData)) {
            return;
          }
          // First check direct programId
          let programId = userData.programId;

          // If no programId, try to get it from cohortId
          if (!programId && userData.cohortId) {
            programId = cohortToProgramMap[userData.cohortId];
          }

          if (programId) {
            learnerCounts[programId] = (learnerCounts[programId] || 0) + 1;
          }
        });

        devLog('Learner counts per program:', learnerCounts);

        const allPrograms = programsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.data().title || 'Unnamed Program',
          learnerCount: learnerCounts[doc.id] || 0,
          ...doc.data()
        }));

        devLog(`Loaded ${allPrograms.length} programs for admin:`, allPrograms.map(p => `${p.name}: ${p.learnerCount} learners`));
        setPrograms(allPrograms);

        if (allPrograms.length === 0) {
          showAlert('info', 'No programs found in the system.');
        }
        return;
      }

      // For non-admin users, use the Firebase Function to get assigned programs
      // Check if functions is available or if we should use mock data
      if (!functions) {
        devLog('Functions not available - loading mock data');
        showAlert('info', 'Loading sample programs for testing');
        setPrograms([
          { id: 'mock-program-1', name: 'Sample Program 1', learnerCount: 25 },
          { id: 'mock-program-2', name: 'Sample Program 2', learnerCount: 30 },
          { id: 'mock-program-3', name: 'Sample Program 3', learnerCount: 15 }
        ]);
        return;
      }

      // Development environment detection - use mock data for localhost
      const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      if (isLocalDevelopment) {
        devLog('Development environment detected - using mock data to avoid CORS issues');
        showAlert('info', 'Using sample programs for local development');
        setPrograms([
          { id: 'mock-program-1', name: 'Development Program 1', learnerCount: 25 },
          { id: 'mock-program-2', name: 'Development Program 2', learnerCount: 30 },
          { id: 'mock-program-3', name: 'Development Program 3', learnerCount: 15 }
        ]);
        return;
      }

      devLog('Calling Firebase Function with authenticated user:', currentUser.uid);

      // Ensure auth token is fresh before calling
      try {
        const token = await currentUser.getIdToken(true); // Force refresh
        devLog('Auth token refreshed successfully, token length:', token.length);

        // Small delay to ensure auth context is properly set
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (authError) {
        devError('Failed to refresh auth token:', authError);
        throw new Error('Authentication token refresh failed');
      }

      // Call Firebase Functions - now properly deployed with region specified
      const getFacilitatorPrograms = httpsCallable(functions, 'getFacilitatorPrograms');
      const result = await getFacilitatorPrograms({});

      devLog('Function result:', result);

      if (result.data.success) {
        setPrograms(result.data.programs);
        if (result.data.programs.length === 0) {
          showAlert('info', 'No programs found. You may need to be assigned to programs first.');
        }
      } else {
        throw new Error('Function returned unsuccessful response');
      }
    } catch (error) {
      devError('Error loading programs:', error);
      devError('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });

      // Only fallback to mock data if it's a development environment issue
      if (error.message.includes('internal') || error.message.includes('unauthenticated') || error.code === 'unauthenticated') {
        devLog('Falling back to mock data due to authentication/configuration issue');
        showAlert('warning', 'Using sample programs (authentication issue detected)');
        setPrograms([
          { id: 'mock-program-1', name: 'Sample Program 1', learnerCount: 25 },
          { id: 'mock-program-2', name: 'Sample Program 2', learnerCount: 30 },
          { id: 'mock-program-3', name: 'Sample Program 3', learnerCount: 15 }
        ]);
      } else {
        showAlert('danger', 'Error loading programs: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCohorts = async () => {
    try {
      setLoading(true);
      // External facilitators cannot query "all cohorts/learners" and then filter (rules will deny).
      // Query only cohorts within their assignment scope, and count learners by scoped queries.
      if (externalFacilitatorLimited) {
        const assignedCohorts = Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [];
        const assignedPrograms = Array.isArray(userProfile?.assignedPrograms) ? userProfile.assignedPrograms : [];

        const cohortsRef = collection(db, 'cohorts');
        const cohortDocs = [];

        if (assignedCohorts.length) {
          for (let i = 0; i < assignedCohorts.length; i += 10) {
            const chunk = assignedCohorts.slice(i, i + 10);
            const snap = await getDocs(query(cohortsRef, where('__name__', 'in', chunk)));
            cohortDocs.push(...snap.docs);
          }
        } else if (assignedPrograms.length) {
          for (let i = 0; i < assignedPrograms.length; i += 10) {
            const chunk = assignedPrograms.slice(i, i + 10);
            const snap = await getDocs(query(cohortsRef, where('programId', 'in', chunk)));
            cohortDocs.push(...snap.docs);
          }
        }

        let cohortList = cohortDocs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        cohortList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        const cohortCounts = {};
        const cohortIds = cohortList.map((c) => c.id).filter(Boolean);
        const usersRef = collection(db, 'users');
        for (let i = 0; i < cohortIds.length; i += 10) {
          const chunk = cohortIds.slice(i, i + 10);
          const learnersSnapshot = await getDocs(query(
            usersRef,
            where('role', '==', 'learner'),
            where('cohortId', 'in', chunk)
          ));
          learnersSnapshot.docs.forEach((docSnap) => {
            const data = docSnap.data();
            if (!isActiveLearner(data)) return;
            if (data.cohortId) {
              cohortCounts[data.cohortId] = (cohortCounts[data.cohortId] || 0) + 1;
            }
          });
        }

        cohortList = cohortList.map((cohort) => ({
          ...cohort,
          learnerCount: cohort.learnerCount || cohortCounts[cohort.id] || 0
        }));

        setCohorts(cohortList);
        return;
      }

      const cohortsSnapshot = await getDocs(collection(db, 'cohorts'));
      let cohortList = cohortsSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      if (userProfile?.role !== 'admin') {
        if (userProfile?.assignedCohorts?.length) {
          cohortList = cohortList.filter(cohort => userProfile.assignedCohorts.includes(cohort.id));
        } else if (userProfile?.assignedPrograms?.length) {
          cohortList = cohortList.filter(cohort => userProfile.assignedPrograms.includes(cohort.programId));
        }
      }

      cohortList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      const learnersSnapshot = await getDocs(query(collection(db, 'users'), where('role', '==', 'learner')));
      const cohortCounts = {};
      learnersSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (!isActiveLearner(data)) {
          return;
        }
        if (data.cohortId) {
          cohortCounts[data.cohortId] = (cohortCounts[data.cohortId] || 0) + 1;
        }
      });
      cohortList = cohortList.map(cohort => ({
        ...cohort,
        learnerCount: cohort.learnerCount || cohortCounts[cohort.id] || 0
      }));
      setCohorts(cohortList);
    } catch (error) {
      devError('Error loading cohorts:', error);
      showAlert('warning', 'Unable to load cohorts. Try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    if (type === 'csv') {
      // Handle CSV file for recipients
      if (!file.name.endsWith('.csv')) {
        showAlert('danger', 'Please upload a CSV file');
        return;
      }

      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const csvContent = e.target.result;
          const base64Data = btoa(csvContent);
          setCsvData(base64Data);
          setCsvFile(file);

          // Validate CSV
          try {
            const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

            if (!functions || isLocalDevelopment) {
              // Mock CSV validation for development
              const mockRecipients = [
                { email: 'user1@example.com', displayName: 'User 1', hasCredentials: true },
                { email: 'user2@example.com', displayName: 'User 2', hasCredentials: true },
                { email: 'user3@example.com', displayName: 'User 3', hasCredentials: true }
              ];
              setCsvRecipients(mockRecipients);
              showAlert('success', `CSV loaded successfully. Found ${mockRecipients.length} recipients (${isLocalDevelopment ? 'development mode' : 'testing mode'}).`);
              return;
            }

            const validateEmailCSV = httpsCallable(functions, 'validateEmailCSV');
            const result = await validateEmailCSV({ csvData: base64Data });

            if (result.data.success) {
              setCsvRecipients(result.data.recipients);
              showAlert('success', `CSV loaded successfully. Found ${result.data.recipientCount} recipients.`);
            } else {
              throw new Error('CSV validation failed');
            }
          } catch (error) {
            devError('CSV validation error:', error);

            // Fallback to mock CSV data
            devLog('Falling back to mock CSV data due to error:', error.message);
            const mockRecipients = [
              { email: 'user1@example.com', displayName: 'User 1', hasCredentials: true },
              { email: 'user2@example.com', displayName: 'User 2', hasCredentials: true },
              { email: 'user3@example.com', displayName: 'User 3', hasCredentials: true }
            ];
            setCsvRecipients(mockRecipients);
            showAlert('warning', `CSV validation failed, using mock data. Found ${mockRecipients.length} recipients (testing mode).`);
          }
        };
        reader.readAsText(file);
      } catch (error) {
        showAlert('danger', 'Error reading CSV file: ' + error.message);
      }
    } else if (type === 'attachment') {
      // Handle attachment files
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        showAlert('danger', 'File size must be less than 10MB');
        return;
      }

      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64Content = e.target.result.split(',')[1]; // Remove data URL prefix
          const newAttachment = {
            filename: file.name,
            contentType: file.type,
            content: base64Content,
            size: file.size
          };

          setEmailData(prev => ({
            ...prev,
            attachments: [...prev.attachments, newAttachment]
          }));

          showAlert('success', `Attachment "${file.name}" added successfully`);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        showAlert('danger', 'Error processing attachment: ' + error.message);
      }
    }
  };

  const removeAttachment = (index) => {
    setEmailData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSendEmail = async () => {
    try {
      if (externalViewerReporting) {
        showAlert('danger', 'External reporting users cannot send emails.');
        return;
      }

      // Validate required fields
      if (!emailData.subject.trim()) {
        showAlert('danger', 'Subject is required');
        return;
      }

      if (!emailData.htmlContent.trim()) {
        showAlert('danger', 'Email content is required');
        return;
      }

      // Validate scheduling if enabled
      if (emailData.isScheduled) {
        if (externalFacilitatorLimited) {
          showAlert('danger', 'External facilitators cannot schedule or create recurring email campaigns.');
          return;
        }
        if (!emailData.scheduledDate || !emailData.scheduledTime) {
          showAlert('danger', 'Please select both date and time for scheduled email');
          return;
        }

        const scheduledDateTime = new Date(`${emailData.scheduledDate}T${emailData.scheduledTime}`);
        if (scheduledDateTime <= new Date()) {
          showAlert('danger', 'Scheduled time must be in the future');
          return;
        }
      }

      const formattedHtml = normalizeEmailHtml(emailData.htmlContent);
      const formattedText = emailData.textContent
        ? emailData.textContent
        : stripHtmlToText(formattedHtml);

      let requestData = {
        subject:       emailData.subject,
        htmlContent:   formattedHtml,
        textContent:   formattedText,
        attachments:   emailData.attachments,
        heading:       emailData.heading       || '',
        subtitle:      emailData.subtitle      || '',
        ctaText:       emailData.ctaText       || '',
        ctaUrl:        emailData.ctaUrl        || '',
        resourceLinks: emailData.resourceLinks || [],
      };

      // Configure recipients based on type
      if (emailData.recipientType === 'program') {
        if (externalFacilitatorLimited) {
          showAlert('danger', 'External facilitators can only email explicitly selected learners.');
          return;
        }
        if (!emailData.selectedProgram) {
          showAlert('danger', 'Please select a program');
          return;
        }
        requestData.recipients = 'all_program';
        requestData.programId = emailData.selectedProgram;
      } else if (emailData.recipientType === 'cohort') {
        if (externalFacilitatorLimited) {
          showAlert('danger', 'External facilitators can only email explicitly selected learners.');
          return;
        }
        const cohortIds = Array.from(
          new Set(
            [
              ...(Array.isArray(emailData.selectedCohorts) ? emailData.selectedCohorts : []),
              emailData.selectedCohort
            ].filter(Boolean)
          )
        );

        if (cohortIds.length === 0) {
          showAlert('danger', 'Please select at least one cohort');
          return;
        }
        requestData.recipients = 'all_cohort';
        requestData.cohortId = cohortIds[0];
        requestData.cohortIds = cohortIds;
      } else if (emailData.recipientType === 'csv') {
        if (externalFacilitatorLimited) {
          showAlert('danger', 'External facilitators cannot use CSV uploads for bulk messaging.');
          return;
        }
        if (!csvData) {
          showAlert('danger', 'Please upload a CSV file');
          return;
        }
        requestData.recipients = 'csv_credentials';
        requestData.csvData = csvData;
        requestData.credentialTemplate = true;
      } else if (emailData.recipientType === 'specific') {
        const emails = emailData.specificEmails
          .split(',')
          .map(email => email.trim())
          .filter(email => email.includes('@'));

        if (emails.length === 0) {
          showAlert('danger', 'Please enter valid email addresses');
          return;
        }
        if (externalFacilitatorLimited && emails.length > 10) {
          showAlert('danger', 'External facilitators can only email up to 10 recipients at a time.');
          return;
        }
        requestData.recipients = emails;
      }

      // Handle scheduled email
      if (emailData.isScheduled) {
        const scheduledDateTime = new Date(`${emailData.scheduledDate}T${emailData.scheduledTime}`);

        const scheduleData = {
          ...requestData,
          scheduledFor: scheduledDateTime,
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
          status: 'pending',
          recipientType: emailData.recipientType,
          recipientCount: getRecipientCount(),
          isRecurring: emailData.isRecurring || false,
          recurringFrequency: emailData.isRecurring ? emailData.recurringFrequency : null,
          recurringEndDate: emailData.isRecurring && emailData.recurringEndDate
            ? new Date(emailData.recurringEndDate)
            : null,
          lastSentAt: null,
          nextScheduledFor: scheduledDateTime
        };

        await addDoc(collection(db, 'scheduledEmails'), scheduleData);

        const message = emailData.isRecurring
          ? `Recurring email scheduled (${emailData.recurringFrequency}) starting ${scheduledDateTime.toLocaleString()}`
          : `Email scheduled for ${scheduledDateTime.toLocaleString()}`;

        showAlert('success', message);
        setShowSendModal(false);

      // Reset form
        setEmailData({
          recipientType: 'program',
          selectedProgram: '',
          selectedCohort: '',
          selectedCohorts: [],
          cohortProgramFilter: '',
          specificEmails: '',
          subject: '',
          htmlContent: '',
          textContent: '',
          attachments: [],
          isScheduled: false,
          scheduledDate: '',
          scheduledTime: '',
          isRecurring: false,
          recurringFrequency: 'weekly',
          recurringEndDate: '',
          heading: '',
          subtitle: '',
          ctaText: '',
          ctaUrl: '',
          resourceLinks: [],
        });
        setCsvFile(null);
        setCsvData('');
        setCsvRecipients([]);

        // Reload scheduled emails
        await loadScheduledEmails();
        await loadSentEmails();
        return;
      }

      setSendProgress({ show: true, current: 0, total: 1 });
      setShowSendModal(true);

      const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      if (!functions || isLocalDevelopment) {
        // Mock email sending for development
        devLog('Simulating email send with mock data');
        setTimeout(() => {
          setSendProgress({ show: false, current: 0, total: 0 });
          const mockResults = {
            total: getRecipientCount(),
            successful: getRecipientCount(),
            failed: 0,
            details: []
          };
          setSendResults(mockResults);
          showAlert('success', `Mock email sent successfully to ${getRecipientCount()} recipients! (${isLocalDevelopment ? 'Development mode' : 'Testing mode'})`);
        }, 1500); // Simulate some delay
      } else {
        try {
          const sendCustomEmail = httpsCallable(functions, 'sendCustomEmail');
          const result = await sendCustomEmail(requestData);

          setSendProgress({ show: false, current: 0, total: 0 });

          if (result.data.success) {
            setSendResults(result.data.results);
            showAlert('success', result.data.message);
            await loadSentEmails();
          } else {
            throw new Error('Send email function returned unsuccessful response');
          }
        } catch (functionError) {
          devError('Firebase Functions error:', functionError);

          // Fallback to mock send on any function error
          devLog('Falling back to mock email send due to error:', functionError.message);
          setSendProgress({ show: false, current: 0, total: 0 });
          const mockResults = {
            total: getRecipientCount(),
            successful: getRecipientCount(),
            failed: 0,
            details: []
          };
          setSendResults(mockResults);
          showAlert('warning', `Email send failed, simulated successful send to ${getRecipientCount()} recipients (Testing mode)`);
        }
      }

      // Reset form on successful send
      setEmailData({
        recipientType: 'program',
        selectedProgram: '',
        selectedCohort: '',
        selectedCohorts: [],
        cohortProgramFilter: '',
        specificEmails: '',
        subject: '',
        htmlContent: '',
        textContent: '',
        attachments: [],
        isScheduled: false,
        scheduledDate: '',
        scheduledTime: '',
        isRecurring: false,
        recurringFrequency: 'weekly',
        recurringEndDate: '',
        heading: '',
        subtitle: '',
        ctaText: '',
        ctaUrl: '',
        resourceLinks: [],
      });
      setCsvFile(null);
      setCsvData('');
      setCsvRecipients([]);

    } catch (error) {
      devError('Error sending email:', error);
      showAlert('danger', 'Error sending email: ' + error.message);
      setSendProgress({ show: false, current: 0, total: 0 });
    }
  };

  const getRecipientCount = () => {
    if (emailData.recipientType === 'program' && emailData.selectedProgram) {
      const program = programs.find(p => p.id === emailData.selectedProgram);
      return program?.learnerCount || 0;
    }
    if (emailData.recipientType === 'cohort') {
      const cohortIds = Array.from(
        new Set(
          [
            ...(Array.isArray(emailData.selectedCohorts) ? emailData.selectedCohorts : []),
            emailData.selectedCohort
          ].filter(Boolean)
        )
      );
      return cohortIds.reduce((total, cohortId) => {
        const cohort = cohorts.find(c => c.id === cohortId);
        return total + (cohort?.learnerCount || 0);
      }, 0);
    }
    if (emailData.recipientType === 'csv') {
      return csvRecipients.length;
    }
    if (emailData.recipientType === 'specific') {
      return emailData.specificEmails.split(',').filter(email => email.trim().includes('@')).length;
    }
    return 0;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const deleteScheduledEmail = async (emailId) => {
    try {
      await deleteDoc(doc(db, 'scheduledEmails', emailId));
      showAlert('success', 'Scheduled email deleted');
      await loadScheduledEmails();
    } catch (error) {
      devError('Error deleting scheduled email:', error);
      showAlert('danger', 'Failed to delete scheduled email');
    }
  };

  const handleResendPreviousEmail = async (emailLog) => {
    try {
      setResendingEmailId(emailLog.id);
      setSendResults(null);
      setShowSendModal(true);
      setSendProgress({ show: true, current: 0, total: 1 });

      const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!functions || isLocalDevelopment) {
        setTimeout(() => {
          setSendProgress({ show: false, current: 0, total: 0 });
          const mockResults = {
            total: emailLog.recipientCount || 0,
            successful: emailLog.recipientCount || 0,
            failed: 0,
            details: []
          };
          setSendResults(mockResults);
          showAlert('success', `Mock resend completed for "${emailLog.subject}"`);
          setResendingEmailId('');
        }, 1200);
        return;
      }

      const resendLoggedCustomEmail = httpsCallable(functions, 'resendLoggedCustomEmail');
      const result = await resendLoggedCustomEmail({ emailLogId: emailLog.id });

      setSendProgress({ show: false, current: 0, total: 0 });
      setSendResults(result.data.results);
      showAlert('success', result.data.message);
      await loadSentEmails();
    } catch (error) {
      devError('Error resending email:', error);
      showAlert('danger', 'Failed to resend previous email: ' + error.message);
      setSendProgress({ show: false, current: 0, total: 0 });
    } finally {
      setResendingEmailId('');
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h4>Custom Email Composer</h4>
              <p className="mb-0 text-muted">Send custom emails to learners with attachments and credentials</p>
            </Card.Header>
            <Card.Body>
              {alert.show && (
                <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false })}>
                  {alert.message}
                </Alert>
              )}

              <Tabs
                activeKey={emailData.recipientType}
                onSelect={(k) => {
                  if (externalFacilitatorLimited && k !== 'specific') {
                    showAlert('warning', 'External facilitators can only email explicitly selected learners.');
                    return;
                  }
                  setEmailData((prev) => ({ ...prev, recipientType: k }));
                }}
              >
                <Tab eventKey="program" title="Program Recipients">
                  <div className="pt-3">
                    {externalFacilitatorLimited && (
                      <Alert variant="warning">
                        External facilitators cannot email an entire program. Use "Specific Emails".
                      </Alert>
                    )}
                    <Form.Group className="mb-3">
                      <Form.Label>Select Program</Form.Label>
                      <Form.Select
                        value={emailData.selectedProgram}
                        onChange={(e) => setEmailData(prev => ({ ...prev, selectedProgram: e.target.value }))}
                      >
                        <option value="">Choose a program...</option>
                        {programs.map(program => (
                          <option key={program.id} value={program.id}>
                            {program.name} ({program.learnerCount} learners)
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Tab>

                <Tab eventKey="cohort" title="Cohort Recipients">
                  <div className="pt-3">
                    {externalFacilitatorLimited && (
                      <Alert variant="warning">
                        External facilitators cannot email an entire cohort. Use "Specific Emails".
                      </Alert>
                    )}
                    <Form.Group className="mb-3">
                      <Form.Label>Filter by Program (optional)</Form.Label>
                      <Form.Select
                        value={emailData.cohortProgramFilter}
                        onChange={(e) => setEmailData(prev => ({
                          ...prev,
                          cohortProgramFilter: e.target.value,
                          selectedCohort: '',
                          selectedCohorts: []
                        }))}
                      >
                        <option value="">All programs</option>
                        {programs.map(program => (
                          <option key={program.id} value={program.id}>
                            {program.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Select Cohorts</Form.Label>
                      <div className="border rounded p-3" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                        {cohorts
                          .filter(cohort => !emailData.cohortProgramFilter || cohort.programId === emailData.cohortProgramFilter)
                          .map(cohort => {
                            const isChecked = (emailData.selectedCohorts || []).includes(cohort.id);
                            return (
                              <Form.Check
                                key={cohort.id}
                                type="checkbox"
                                id={`cohort-recipient-${cohort.id}`}
                                className="mb-2"
                                checked={isChecked}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setEmailData(prev => {
                                    const existing = Array.isArray(prev.selectedCohorts) ? prev.selectedCohorts : [];
                                    const nextSelected = checked
                                      ? Array.from(new Set([...existing, cohort.id]))
                                      : existing.filter(id => id !== cohort.id);
                                    return {
                                      ...prev,
                                      selectedCohort: nextSelected[0] || '',
                                      selectedCohorts: nextSelected
                                    };
                                  });
                                }}
                                label={`${cohort.name || cohort.id} (${cohort.learnerCount || 0} learners)`}
                              />
                            );
                          })}
                        {!cohorts.filter(cohort => !emailData.cohortProgramFilter || cohort.programId === emailData.cohortProgramFilter).length && (
                          <div className="text-muted small">No cohorts available for the selected filter.</div>
                        )}
                      </div>
                      <Form.Text className="text-muted">
                        Select one or more cohorts to send a single email across multiple groups.
                      </Form.Text>
                    </Form.Group>
                  </div>
                </Tab>

                <Tab eventKey="csv" title="CSV with Credentials">
                  <div className="pt-3">
                    {externalFacilitatorLimited && (
                      <Alert variant="warning">
                        External facilitators cannot use CSV uploads for bulk messaging. Use "Specific Emails".
                      </Alert>
                    )}
                    <Form.Group className="mb-3">
                      <Form.Label>Upload CSV File</Form.Label>
                      <Form.Control
                        type="file"
                        accept=".csv"
                        onChange={(e) => handleFileUpload(e, 'csv')}
                      />
                      <Form.Text className="text-muted">
                        CSV should contain columns: email, displayName, username, password, program, cohort
                      </Form.Text>
                    </Form.Group>

                    {csvRecipients.length > 0 && (
                      <Card className="mb-3">
                        <Card.Header>CSV Recipients Preview</Card.Header>
                        <Card.Body>
                          <ListGroup variant="flush" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {csvRecipients.slice(0, 10).map((recipient, index) => (
                              <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                                <span>{recipient.email} ({recipient.displayName})</span>
                                {recipient.hasCredentials ? (
                                  <Badge bg="success">Has Credentials</Badge>
                                ) : (
                                  <Badge bg="warning">No Credentials</Badge>
                                )}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                          {csvRecipients.length > 10 && (
                            <small className="text-muted">And {csvRecipients.length - 10} more...</small>
                          )}
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                </Tab>

                <Tab eventKey="specific" title="Specific Emails">
                  <div className="pt-3">
                    {externalFacilitatorLimited && (
                      <Alert variant="info">
                        External facilitators can only message explicitly selected learners (no programme/cohort broadcasts).
                      </Alert>
                    )}
                    <Form.Group className="mb-3">
                      <Form.Label>Email Addresses</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={emailData.specificEmails}
                        onChange={(e) => setEmailData(prev => ({ ...prev, specificEmails: e.target.value }))}
                        placeholder="Enter email addresses separated by commas"
                      />
                      <Form.Text className="text-muted">
                        Enter multiple emails separated by commas
                      </Form.Text>
                    </Form.Group>
                  </div>
                </Tab>
              </Tabs>

              <Row className="mt-4">
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Subject</Form.Label>
                    <Form.Control
                      type="text"
                      value={emailData.subject}
                      onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Email subject (use {name} for personalization)"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email Content</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={10}
                      value={emailData.htmlContent}
                      onChange={(e) => setEmailData(prev => ({ ...prev, htmlContent: e.target.value }))}
                      placeholder="Enter your email content here. You can use HTML formatting. Use {name} for personalization."
                    />
                    <Form.Text className="text-muted">
                      HTML is supported. Use {`{name}`} to personalise with the recipient's name. Plain URLs are auto-linked.
                    </Form.Text>
                  </Form.Group>

                  {/* ── NPI Template Options ─────────────────────────────── */}
                  <div className="mb-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowTemplateOptions((v) => !v)}
                    >
                      {showTemplateOptions ? '▲ Hide' : '▼ Show'} Template Options
                    </Button>
                  </div>

                  {showTemplateOptions && (
                    <Card className="mb-3 border-0 bg-light">
                      <Card.Body className="py-3">
                        <p className="small text-muted mb-3">
                          These fields fill the NPI-branded wrapper that recipients see. All are optional.
                        </p>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="small fw-semibold">Card Heading</Form.Label>
                              <Form.Control
                                type="text"
                                size="sm"
                                value={emailData.heading}
                                onChange={(e) => setEmailData(prev => ({ ...prev, heading: e.target.value }))}
                                placeholder="e.g. Welcome to Your Programme"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="small fw-semibold">Subtitle</Form.Label>
                              <Form.Control
                                type="text"
                                size="sm"
                                value={emailData.subtitle}
                                onChange={(e) => setEmailData(prev => ({ ...prev, subtitle: e.target.value }))}
                                placeholder="e.g. Your account is ready"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="small fw-semibold">CTA Button Label</Form.Label>
                              <Form.Control
                                type="text"
                                size="sm"
                                value={emailData.ctaText}
                                onChange={(e) => setEmailData(prev => ({ ...prev, ctaText: e.target.value }))}
                                placeholder="e.g. Access Your Dashboard"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="small fw-semibold">CTA Button URL</Form.Label>
                              <Form.Control
                                type="url"
                                size="sm"
                                value={emailData.ctaUrl}
                                onChange={(e) => setEmailData(prev => ({ ...prev, ctaUrl: e.target.value }))}
                                placeholder="https://lms-access70.web.app"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        {/* Resource links */}
                        <Form.Label className="small fw-semibold">Helpful Links</Form.Label>
                        {(emailData.resourceLinks || []).map((link, idx) => (
                          <Row key={idx} className="mb-2 gx-2 align-items-center">
                            <Col>
                              <Form.Control
                                type="text"
                                size="sm"
                                value={link.label}
                                onChange={(e) => {
                                  const next = [...emailData.resourceLinks];
                                  next[idx] = { ...next[idx], label: e.target.value };
                                  setEmailData(prev => ({ ...prev, resourceLinks: next }));
                                }}
                                placeholder="Link label"
                              />
                            </Col>
                            <Col>
                              <Form.Control
                                type="url"
                                size="sm"
                                value={link.url}
                                onChange={(e) => {
                                  const next = [...emailData.resourceLinks];
                                  next[idx] = { ...next[idx], url: e.target.value };
                                  setEmailData(prev => ({ ...prev, resourceLinks: next }));
                                }}
                                placeholder="https://…"
                              />
                            </Col>
                            <Col xs="auto">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  const next = emailData.resourceLinks.filter((_, i) => i !== idx);
                                  setEmailData(prev => ({ ...prev, resourceLinks: next }));
                                }}
                              >×</Button>
                            </Col>
                          </Row>
                        ))}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mt-1"
                          onClick={() => setEmailData(prev => ({
                            ...prev,
                            resourceLinks: [...(prev.resourceLinks || []), { label: '', url: '' }]
                          }))}
                        >
                          + Add link
                        </Button>
                      </Card.Body>
                    </Card>
                  )}
                </Col>

                <Col md={4}>
                  <Card>
                    <Card.Header>
                      <h6>Email Summary</h6>
                    </Card.Header>
                    <Card.Body>
                      <p><strong>Recipients:</strong> {getRecipientCount()}</p>
                      <p><strong>Type:</strong> {emailData.recipientType}</p>
                      <p><strong>Attachments:</strong> {emailData.attachments.length}</p>

                      <hr />

                      {/* Scheduling Section */}
                      <h6>⏰ Schedule Email</h6>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label="Schedule for later"
                          checked={emailData.isScheduled}
                          onChange={(e) => setEmailData(prev => ({ ...prev, isScheduled: e.target.checked }))}
                        />
                      </Form.Group>

                      {emailData.isScheduled && (
                        <>
                          <Form.Group className="mb-2">
                            <Form.Label>Date</Form.Label>
                            <Form.Control
                              type="date"
                              value={emailData.scheduledDate}
                              onChange={(e) => setEmailData(prev => ({ ...prev, scheduledDate: e.target.value }))}
                              min={new Date().toISOString().split('T')[0]}
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Time</Form.Label>
                            <Form.Control
                              type="time"
                              value={emailData.scheduledTime}
                              onChange={(e) => setEmailData(prev => ({ ...prev, scheduledTime: e.target.value }))}
                            />
                          </Form.Group>

                          {emailData.scheduledDate && emailData.scheduledTime && (
                            <Alert variant="info" className="small">
                              Will send on {new Date(`${emailData.scheduledDate}T${emailData.scheduledTime}`).toLocaleString()}
                            </Alert>
                          )}

                          {/* Recurring Options */}
                          {emailData.isScheduled && (
                            <>
                              <Form.Group className="mb-3">
                                <Form.Check
                                  type="checkbox"
                                  label="Make this recurring"
                                  checked={emailData.isRecurring}
                                  onChange={(e) => setEmailData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                                />
                              </Form.Group>

                              {emailData.isRecurring && (
                                <>
                                  <Form.Group className="mb-2">
                                    <Form.Label>Frequency</Form.Label>
                                    <Form.Select
                                      value={emailData.recurringFrequency}
                                      onChange={(e) => setEmailData(prev => ({ ...prev, recurringFrequency: e.target.value }))}
                                    >
                                      <option value="daily">Daily</option>
                                      <option value="weekly">Weekly</option>
                                      <option value="monthly">Monthly</option>
                                    </Form.Select>
                                  </Form.Group>

                                  <Form.Group className="mb-3">
                                    <Form.Label>End Date (Optional)</Form.Label>
                                    <Form.Control
                                      type="date"
                                      value={emailData.recurringEndDate}
                                      onChange={(e) => setEmailData(prev => ({ ...prev, recurringEndDate: e.target.value }))}
                                      min={emailData.scheduledDate}
                                    />
                                    <Form.Text className="text-muted">
                                      Leave empty for indefinite recurrence
                                    </Form.Text>
                                  </Form.Group>

                                  <Alert variant="warning" className="small">
                                    This email will be sent <strong>{emailData.recurringFrequency}</strong>
                                    {emailData.recurringEndDate
                                      ? ` until ${new Date(emailData.recurringEndDate).toLocaleDateString()}`
                                      : ' indefinitely'}
                                  </Alert>
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}

                      <hr />

                      <h6>Attachments</h6>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="file"
                          onChange={(e) => handleFileUpload(e, 'attachment')}
                          size="sm"
                        />
                        <Form.Text className="text-muted">Max 10MB per file</Form.Text>
                      </Form.Group>

                      {emailData.attachments.map((attachment, index) => (
                        <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                          <div>
                            <small><strong>{attachment.filename}</strong></small><br/>
                            <small className="text-muted">{formatFileSize(attachment.size)}</small>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeAttachment(index)}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center mt-4">
                <Button
                  variant="outline-primary"
                  onClick={() => setShowScheduledModal(true)}
                >
                  View Scheduled Emails ({scheduledEmails.length})
                </Button>
                <Button
                  variant="outline-secondary"
                  className="ms-2"
                  onClick={() => setShowSentEmailsModal(true)}
                >
                  Recent Sent Emails ({sentEmails.length})
                </Button>

                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => setShowSendModal(true)}
                  disabled={!emailData.subject || !emailData.htmlContent || getRecipientCount() === 0}
                >
                  {emailData.isScheduled ? 'Schedule Email' : 'Send Now'} to {getRecipientCount()} Recipients
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Send Confirmation Modal */}
      <Modal show={showSendModal} onHide={() => setShowSendModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Email Send</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sendProgress.show ? (
            <div className="text-center">
              <div className="spinner-border mb-3" role="status">
                <span className="visually-hidden">Sending emails...</span>
              </div>
              <p>Sending emails...</p>
              <ProgressBar now={50} label="Processing..." />
            </div>
          ) : sendResults ? (
            <div>
              <Alert variant={sendResults.failed > 0 ? 'warning' : 'success'}>
                <h6>Send Results:</h6>
                <p>Successfully sent: {sendResults.successful}</p>
                {sendResults.failed > 0 && <p>Failed to send: {sendResults.failed}</p>}
                <p>Total processed: {sendResults.total}</p>
              </Alert>

              {sendResults.details && sendResults.details.filter(d => !d.success).length > 0 && (
                <div>
                  <h6>Failed Emails:</h6>
                  <ListGroup>
                    {sendResults.details.filter(d => !d.success).map((result, index) => (
                      <ListGroup.Item key={index} variant="danger">
                        {result.email}: {result.error}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p><strong>Subject:</strong> {emailData.subject}</p>
              <p><strong>Recipients:</strong> {getRecipientCount()}</p>
              <p><strong>Attachments:</strong> {emailData.attachments.length}</p>

              {emailData.recipientType === 'csv' && (
                <Alert variant="info">
                  This email will include login credentials for each recipient from the CSV file.
                </Alert>
              )}

              <p>Are you sure you want to send this email?</p>
              <Card className="mt-3">
                <Card.Header>
                  <strong>Branded Email Preview</strong>
                  {emailData.subject && (
                    <span className="text-muted ms-2 small">
                      Subject: {emailData.subject.replace(/\{name\}/g, 'Learner')}
                    </span>
                  )}
                </Card.Header>
                <Card.Body className="p-0">
                  {previewHtml ? (
                    <iframe
                      title="Email preview"
                      srcDoc={previewHtml}
                      style={{ width: '100%', height: '360px', border: 'none', display: 'block' }}
                      sandbox="allow-same-origin"
                    />
                  ) : (
                    <p className="p-3 text-muted small mb-0">Enter content above to preview the email.</p>
                  )}
                </Card.Body>
              </Card>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!sendProgress.show && !sendResults && (
            <>
              <Button variant="secondary" onClick={() => setShowSendModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSendEmail}>
                Send Email
              </Button>
            </>
          )}
          {sendResults && (
            <Button variant="primary" onClick={() => {
              setShowSendModal(false);
              setSendResults(null);
            }}>
              Close
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Scheduled Emails Modal */}
      <Modal show={showScheduledModal} onHide={() => setShowScheduledModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Scheduled Emails</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {scheduledEmails.length === 0 ? (
            <Alert variant="info">
              No scheduled emails. Schedule an email using the "Schedule for later" option when composing.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Recipients</th>
                    <th>Scheduled For</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledEmails.map(email => (
                    <tr key={email.id}>
                      <td>
                        <strong>{email.subject}</strong>
                        <br />
                        <small className="text-muted">
                          {email.htmlContent?.substring(0, 100)}...
                        </small>
                      </td>
                      <td>
                        <Badge bg="primary">{email.recipientCount || 0}</Badge>
                        <br />
                        <small>{email.recipientType}</small>
                      </td>
                      <td>
                        <strong>{email.scheduledFor?.toLocaleString()}</strong>
                        <br />
                        <small className="text-muted">
                          {email.scheduledFor > new Date()
                            ? `In ${Math.ceil((email.scheduledFor - new Date()) / (1000 * 60 * 60))} hours`
                            : 'Overdue'
                          }
                        </small>
                      </td>
                      <td>
                        <Badge bg={email.status === 'pending' ? 'warning' : 'success'}>
                          {email.status}
                        </Badge>
                        {email.isRecurring && (
                          <>
                            <br />
                            <Badge bg="info" className="mt-1">
                              {email.recurringFrequency}
                            </Badge>
                          </>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this scheduled email?')) {
                              deleteScheduledEmail(email.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowScheduledModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSentEmailsModal} onHide={() => setShowSentEmailsModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Recent Sent Emails</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sentEmails.length === 0 ? (
            <Alert variant="info">
              No sent LMS emails found yet.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Recipients</th>
                    <th>Attachments</th>
                    <th>Sent</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sentEmails.map((email) => (
                    <tr key={email.id}>
                      <td>
                        <strong>{email.subject}</strong>
                        <br />
                        <small className="text-muted">
                          {(email.htmlContent || '').replace(/<[^>]*>/g, '').substring(0, 100)}...
                        </small>
                      </td>
                      <td>
                        <Badge bg="primary">{email.recipientCount || 0}</Badge>
                        <br />
                        <small className="text-muted">{Array.isArray(email.recipients) ? 'specific' : String(email.recipients || 'saved')}</small>
                      </td>
                      <td>
                        <Badge bg={email.attachmentCount > 0 ? 'info' : 'secondary'}>
                          {email.attachmentCount || 0}
                        </Badge>
                      </td>
                      <td>
                        <strong>{email.timestamp ? email.timestamp.toLocaleString() : 'Unknown'}</strong>
                      </td>
                      <td>
                        <Badge bg={email.failureCount > 0 ? 'warning' : 'success'}>
                          {email.failureCount > 0 ? `${email.successCount || 0} sent / ${email.failureCount} failed` : 'Sent'}
                        </Badge>
                        {email.type === 'custom_email_resend' ? (
                          <>
                            <br />
                            <Badge bg="secondary" className="mt-1">Resend</Badge>
                          </>
                        ) : null}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={!email.resendPayload || resendingEmailId === email.id}
                          onClick={() => handleResendPreviousEmail(email)}
                        >
                          {resendingEmailId === email.id ? 'Resending...' : 'Resend'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSentEmailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CustomEmailComposer;
